import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  getCountsPerDay,
  getMeanSuccessRate,
  getTotalCalls,
  getTotalCallsPerMonth,
  getTotalDuration,
  getTotalRows,
  getUniqueConversations,
} from '../../helpers/dashboardHelper';
import { useTranslation } from '../useTranslation';
import { Agent, AgentType } from '../../interfaces/Agent';
import { Conversation, DateRangeDate } from '../../interfaces/Analytics';
import { useConversationsTableState } from '../useConversationsTableState';
import { useIsRefreshing } from '../useIsRefreshing';
import { useDashboardAgents } from './useDashboardAgents';
import { useDashboardData } from './useDashboardData';

const fillOutAgentNames = (
  conversations: Conversation[],
  agentsList: Agent[],
  unknownAgentLabel: string
): Conversation[] => {
  return conversations.map(conversation => {
    const agent = agentsList.find(agent => agent.id === conversation.agent_id);
    return {
      ...conversation,
      agent_name: agent ? agent.name : unknownAgentLabel,
    };
  });
};

/**
 * Hook for single agent dashboard (used in ShowAgent page)
 */
export const useSingleAgentDashboard = (
  companyId: string | undefined,
  userId: string | undefined,
  agentId: string | undefined
) => {
  const { t } = useTranslation();

  // Set the default date range to the last 15 days
  const [dateRange, setDateRange] = useState<[DateRangeDate, DateRangeDate]>([
    dayjs().subtract(14, 'day'),
    dayjs(),
  ]);

  const table = useConversationsTableState(AgentType.CALL);
  const changeDateRange = (range: [DateRangeDate, DateRangeDate]) => {
    setDateRange(range);
    table.backToFirstPage();
  };

  // Fetch agent details
  const { data: agentsList } = useDashboardAgents(!!agentId);

  // Fetch dashboard data for single agent
  const {
    data: rawDashboardData,
    refetch,
    isLoading,
    isFetching,
    error,
  } = useDashboardData({
    companyId,
    userId,
    startDate: dateRange[0]?.format('YYYY-MM-DD'),
    endDate: dateRange[1]?.format('YYYY-MM-DD'),
    agentIds: agentId,
    enabled: !!agentId,
    paging: table.paging,
  });

  // The cards only change with the range, not with a page, sort or search
  const isRefreshingMetrics = useIsRefreshing(
    `${dateRange[0]?.format('YYYY-MM-DD')}|${dateRange[1]?.format('YYYY-MM-DD')}`,
    isFetching
  );

  // Calculate metrics from dashboard data
  const agentsOverAllMetrics = useMemo(() => {
    if (!rawDashboardData) {
      return {
        meanSuccessRate: 0,
        totalCalls: 0,
        totalDuration: 0,
      };
    }
    return {
      meanSuccessRate: getMeanSuccessRate(rawDashboardData),
      totalCalls: getTotalCalls(rawDashboardData),
      totalDuration: getTotalDuration(rawDashboardData),
    };
  }, [rawDashboardData]);

  // Calculate calls per month
  const callsPerMonth = useMemo(() => {
    if (!rawDashboardData) {
      return {};
    }
    return getTotalCallsPerMonth(rawDashboardData);
  }, [rawDashboardData]);

  const callsPerDay = useMemo(
    () => getCountsPerDay(rawDashboardData, 'daily_calls_number'),
    [rawDashboardData]
  );

  // Get conversations with agent names
  const conversations = useMemo(() => {
    if (!rawDashboardData || !agentsList) {
      return [];
    }
    return fillOutAgentNames(
      getUniqueConversations(rawDashboardData),
      agentsList,
      t('Unknown Agent')
    );
  }, [rawDashboardData, agentsList, t]);

  return {
    agentsOverAllMetrics,
    callsPerMonth,
    callsPerDay,
    conversations,
    dateRange,
    isRefreshingMetrics,
    // For live sync
    refetch,
    isLoading,
    isFetching,
    error,
    setDateRange: changeDateRange,
    serverPaging: table.serverPaging(
      getTotalRows(rawDashboardData, agentsOverAllMetrics.totalCalls),
      isFetching
    ),
    paginationModel: table.paginationModel,
    setPaginationModel: table.setPaginationModel,
    setSortModel: table.setSortModel,
    setSearch: table.setSearch,
  };
};
